import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Search, MapPin, Plus, X } from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/input";
import SharedHeader from "../Headers/SharedHeader";
import SharedFooter3 from "../Footer/SharedFooter3";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import SharedHeader11 from "../Headers/SharedHeader11";
import { useSelector } from "react-redux";
import { selectLoaderValue } from "../redux/Slices/loadingSlice";
import { getUserCategories, useAppDispatch, createUserCategories } from "../redux";
import Spinner from "../modules/shared/components/Spinner";
import { RoleEnum, TypeEnum } from "../modules/shared/model";
import { useNavigate } from "react-router-dom";
import CountryDropdown from "../components/ui/CountryDropdown";
import { fetchCountries, fetchRegionsByCountry, clearRegions, clearCities } from "../redux/Slices/LocationSlice";
import { LocationCountry, LocationRegion, LocationCity } from "../modules/shared/model";
import { getTranslatedCategoryName } from "../utils/categoryTranslation";


const CategoriesAndLocation = () => {
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language;
    const [selectedType, setSelectedType] = useState("local-home");
    const [searchQuery, setSearchQuery] = useState("");
    const [locationQuery, setLocationQuery] = useState("");
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [newCategory, setNewCategory] = useState("");
    const [newTags, setNewTags] = useState("");
    const [regions, setRegions] = useState([]);
    const [customCategories, setCustomCategories] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState<LocationCountry | null>(null);
    
    // Convert LocationCountry to Country interface expected by CountryDropdown
    const convertToCountryDropdownFormat = (locationCountry: LocationCountry | null) => {
        if (!locationCountry) return null;
        return {
            id: locationCountry.id,
            name: locationCountry.name,
            code: locationCountry.initial || locationCountry.name.substring(0, 2).toUpperCase()
        };
    };
    
    const { categories: realCategories } = useSelector((state: any) => state.admin);
    const { user } = useSelector((state: any) => state.auth);
    const isLoading = useSelector(selectLoaderValue);
    const { countries, cities, countriesLoading, regionsLoading, citiesLoading } = useSelector((state: any) => state.locations);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    useEffect(() => {
        getCategoryData();
    }, [selectedType]);
    
    useEffect(() => {
        // Fetch countries once
        dispatch(fetchCountries({page:1, limit: 1 }));
    }, [dispatch]);

    // Load cached country after countries are fetched
    useEffect(() => {
        if (countries && countries.length > 0 && !selectedCountry) {
            // Check both caching systems
            const cachedCountry = localStorage.getItem("selectedCountry");
            const cachedCountryId = localStorage.getItem("user_country_preference");
            
            let countryToLoad = null;
            
            // First try our custom cache
            if (cachedCountry) {
                try {
                    const countryData = JSON.parse(cachedCountry);
                    if (countryData && countryData.id) {
                        countryToLoad = countries.find((c: LocationCountry) => c.id === countryData.id);
                    }
                } catch (error) {
                    console.error("Error parsing cached country:", error);
                    localStorage.removeItem("selectedCountry");
                }
            }
            
            // If not found, try the currencyUtils cache
            if (!countryToLoad && cachedCountryId) {
                try {
                    const countryIdData = JSON.parse(cachedCountryId);
                    if (countryIdData && countryIdData.countryId) {
                        countryToLoad = countries.find((c: LocationCountry) => c.id === countryIdData.countryId);
                    }
                } catch (error) {
                    console.error("Error parsing cached country ID:", error);
                    localStorage.removeItem("user_country_preference");
                }
            }
            
            if (countryToLoad) {
                setSelectedCountry(countryToLoad);
                dispatch(fetchRegionsByCountry(countryToLoad.id));
                console.log("Loaded cached country:", countryToLoad.name);
            }
        }
    }, [countries, selectedCountry, dispatch]);
    
    const getCategoryData = async () => {
        const typeId = selectedType === "local-home" ? 1 : selectedType === "local-professional" ? 2 : 3;
        await dispatch(getUserCategories({ typeId }));
    };

    const categories = {
        "local-home": [
            // Home Maintenance & Repair
            "Plumber",
            "Electrician",
            "Carpenter",
            "Gardener",
            "Gardening",
            "Painter",
            "Painting",
            "Handyman",
            "Glazier",
            "Locksmith",
            "System Engineer",
            "Bricklayer",
            "Drywaller",
            "Tiler",
            "Assembler",
            "Furniture Assembly",

            // Cleaning Services
            "Cleaner",
            "Cleaning",
            "Cleaning Company",
            "House Cleaning",
            "Apartment Cleaning",
            "Deep Cleaning",
            "Window Cleaning",
            "Window Glass Cleaning",
            "Carpet Cleaning",
            "Mattress Cleaning",
            "Sofa Cleaning",
            "Home Sofa Cleaning",
            "Floor Cleaning",
            "Post Renovation Cleaning",
            "Office Cleaning",
            "B&B Cleaning",
            "Domestic Cleaning",
            "Environmental Sanitization",
            "Holiday Home Cleaning",
            "Condominium Cleaning",
            "Condominium Stair Cleaning",

            // Installation & Systems
            "Electrical Installer",
            "Air Conditioning Installation",
            "Air Conditioning Assistance",
            "Electrical System Installation",
            "Plumbing Installation",
            "Shower Installation",
            "Sink and Tap Installation",
            "Video Surveillance",
            "Solar Panels",
            "Photovoltaic",
            "Renewable Energy",
            "Gate Automation",
            "Video Intercom Installation",
            "Underfloor Heating Installation",
            "Radiator Installation",

            // Maintenance & Repair
            "Boiler Services",
            "Boiler Repair",
            "Boiler Installation",
            "Boiler Maintenance",
            "Boiler Replacement",
            "Boiler Overhaul",
            "Air Conditioning Maintenance",
            "Water Heater Maintenance",
            "Radiator Maintenance",
            "Air Conditioning Assembly",
            "Air Conditioning Cleaning",
            "Air Conditioning Gas Refill",
            "Appliance Repair",
            "Computer Repair",
            "Air Conditioner Repair",
            "Plumbing Repair",
            "Dishwasher Repair",
            "Washing Machine Repair",
            "Faucet Repair",
            "Electronic Board Repair",
            "Pipe Repair",
            "Data Recovery",
            "PC Assembly",
            "Internet Device Connection Assistance",

            // Renovation & Construction
            "Bathroom Renovation",
            "Kitchen Remodeling",
            "House Renovation",
            "Renovations",
            "Wall Plastering",
            "Wall Repair",
            "Electrical System Renovation",
            "Custom Furniture",
            "Custom Wardrobe",
            "Furniture Restoration",
            "Interior Wall Decoration",
            "Interior Design",
            "Floor Installation",
            "Drywall False Ceiling",
            "Drywall Bookcase",
            "Drywall Partition Walls",
            "Construction Company",

            // Moving & Storage
            "Moving Services",
            "Small Removals",
            "Furniture Transport",
            "Storage Services",
            "Self Storage",
            "Furniture Disposal",
            "Used Furniture Pickup",
            "Clearance",
            "Apartment Clearance",
            "Asbestos Disposal",
            "Waste Disposal",
            "Cellular Clearance",
            "Relocation",
            "Furniture Removal",
        ],
        "local-professional": [
            // Legal & Professional Services
            "Lawyer",
            "Civil Lawyer",
            "Notary",

            // Healthcare & Wellness
            "Psychologist",
            "Child Psychologist",
            "Online Psychologist",
            "Psychologist for Adolescents",
            "Psychotherapist",
            "Marriage Counselor",
            "Dietician",
            "Online Dietitian",
            "Physiotherapist",
            "Physiotherapy",
            "Nutritionist",
            "Online Nutritionist",
            "Osteopath",
            "Sexologist",

            // Therapy & Counseling
            "Couple Therapy",
            "Online Therapy",
            "Psychotherapy",
            "Cognitive Behavioral Psychotherapy",

            // Personal Training & Fitness
            "Personal Trainer",
            "Home Personal Trainer",
            "Online Personal Trainer",

            // Massage Therapy
            "Massage",
            "Massage Therapist",
            "Anti-Cellulite Massage",
            "Ayurvedic Massage",
            "Californian Massage",
            "Decontracting Massage",
            "Lymphatic Drainage Massage",
            "Holistic Massage",
            "Relaxing Massage",
            "Shiatsu Massage",
            "Sports Massage",
            "Therapeutic Massage",
            "Massage for Men",

            // Transportation Services
            "Airport Transfer",
            "Chauffeur Service",

            // Event Services
            "Childrens Birthday Entertainment",
        ],
        international: [
            // Tech & Development
            "Web Development",
            "Mobile Development",
            "Web Developer",
            "Mobile App Developer",
            "IT Support Specialist",

            // Marketing & Digital
            "Digital Marketing",
            "Advertising Marketing",
            "Google Specialist",
            "Google Adwords Specialist",
            "Social Marketing Specialist",
            "Social Media Manager",

            // Business & Legal
            "Business Consulting",
            "Business Consultant",
            "Legal Services",
            "Financial Advisor",
            "Lawyer",

            // Language Services
            "Language Tutor",
            "Translator",

            // Creative Services
            "Design",
            "Writing",
            "Content Writer",
            "Graphic Designer",

            // Event Services
            "Wedding Planner",
            "Event Organizer",
        ],
    };

    const handleAddCategory = async () => {
        if (newCategory.trim() === "") return;
        const tags = newTags
            .split(",")
            .map(tag => tag.trim())
            .filter(tag => tag !== "");

        const typeId =
            selectedType === "local-home"
                ? TypeEnum.Home_Service
                : selectedType === "local-professional"
                ? TypeEnum.Professional_Service
                : TypeEnum.International_Service;

        const payload = {
            name: newCategory.trim(),
            description: "",
            requirements: [],
            subcategories: tags,
            isRequireVerification: true,
            isEmergency: false,
            type: { id: Number(typeId) },
        };

        const res: any = await dispatch(createUserCategories({ data: payload }));
        if (createUserCategories.fulfilled.match(res)) {
            const typeId = selectedType === "local-home" ? 1 : selectedType === "local-professional" ? 2 : 3;
            await dispatch(getUserCategories({ typeId }));
            setNewCategory("");
            setNewTags("");
            setIsDialogOpen(false);
        }
    };
    const getFilteredRealCategories = () => {
        if (!realCategories || !realCategories.data) return [];

        return realCategories.data.filter(category => {
            const typeId = Number(category?.type?.id);
            if (selectedType === "local-home") return typeId === TypeEnum.Home_Service;
            if (selectedType === "local-professional") return typeId === TypeEnum.Professional_Service;
            if (selectedType === "international") return typeId === TypeEnum.International_Service;
            return false;
        });
    };
    const filteredCategories = [
        // ...(categories[selectedType] || []),
        // ...customCategories.filter(cat => cat.type === selectedType).map(cat => cat.name),
        ...getFilteredRealCategories().map(cat => getTranslatedCategoryName(cat, currentLang)),
    ].filter(category => category.toLowerCase().includes(searchQuery.toLowerCase()));

    const handleCountrySelect = (country: any) => {
        
        // Find the corresponding LocationCountry from the countries array
        const locationCountry = countries.find((c: LocationCountry) => c.id === country?.id) || country;
        console.log("Country:", locationCountry);
        setSelectedCountry(locationCountry);
        setLocationQuery(""); // Reset location query when country changes
        
        // Cache the selected country in both systems
        if (locationCountry) {
            try {
                // Cache in our custom system
                localStorage.setItem("selectedCountry", JSON.stringify(locationCountry));
                
                // Cache in the currencyUtils system for consistency
                const cacheData = {
                    countryId: locationCountry.id,
                    timestamp: new Date().toISOString()
                };
                localStorage.setItem("user_country_preference", JSON.stringify(cacheData));
                setRegions(locationCountry.regions);
                console.log("Country cached:", locationCountry.name);
            } catch (error) {
                console.error("Error caching country:", error);
            }
            
            // Clear previous regions and cities
            dispatch(clearRegions());
            dispatch(clearCities());
            
            // Fetch regions for the selected country
            dispatch(fetchRegionsByCountry(locationCountry.id));
        } else {
            // Clear both cache systems if no country selected
            localStorage.removeItem("selectedCountry");
            localStorage.removeItem("user_country_preference");
            
            // Clear all location data if no country selected
            dispatch(clearRegions());
            dispatch(clearCities());
        }
    };

    return (
        <>
            {/* Loading Overlay */}
            {isLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-20 z-50 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center gap-3">
                        <Spinner color="green" />
                        <p className="text-gray-600 text-sm">{t("categoriesPage.loading")}</p>
                    </div>
                </div>
            )}
            
            <div className="flex flex-col min-h-screen bg-white" style={{ textAlign: "left" }}>
                <SharedHeader11 />

                <main className="flex-grow max-w-7xl mx-auto px-4 py-8 w-full">
                    {/* Type Selector - Fixed to display horizontally */}
                    <div className="flex justify-center mb-8">
                        <div className="inline-flex rounded-md shadow-sm">
                            <button
                                onClick={() => setSelectedType("local-home")}
                                className={`px-6 py-2 rounded-l-md border border-r-0 ${selectedType === "local-home"
                                    ? "bg-blue-600 text-white border-blue-600"
                                    : "text-gray-600 hover:bg-gray-100 border-gray-200"
                                    }`}>
                                {t("categoriesPage.typeSelector.homeServices")}
                            </button>
                            <button
                                onClick={() => setSelectedType("local-professional")}
                                className={`px-6 py-2 border-y ${selectedType === "local-professional"
                                    ? "bg-blue-600 text-white border-blue-600"
                                    : "text-gray-600 hover:bg-gray-100 border-gray-200"
                                    }`}>
                                {t("categoriesPage.typeSelector.professionalServices")}
                            </button>
                            <button
                                onClick={() => setSelectedType("international")}
                                className={`px-6 py-2 rounded-r-md border border-l-0 ${selectedType === "international"
                                    ? "bg-blue-600 text-white border-blue-600"
                                    : "text-gray-600 hover:bg-gray-100 border-gray-200"
                                    }`}>
                                {t("categoriesPage.typeSelector.international")}
                            </button>
                        </div>
                    </div>

                    {/* Search Section */}
                    <div className="grid md:grid-cols-2 gap-4 mb-8">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                            <input
                                type="text"
                                placeholder={t("categoriesPage.search.categoriesPlaceholder")}
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        <div className="relative">
                            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                            <input
                                type="text"
                                placeholder={t("categoriesPage.search.locationPlaceholder")}
                                value={locationQuery}
                                onChange={e => setLocationQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                disabled={selectedType === "international"} // Disable for international
                            />
                        </div>
                    </div>

                    {/* Add Category Button */}
                    <button
                        onClick={() => user?.role?.id === Number(RoleEnum.admin) ? navigate("/admin/categories") : setIsDialogOpen(true)}
                        className="mb-8 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                        <Plus className="h-4 w-4 mr-2" />
                        {t("categoriesPage.addNewCategory")}
                    </button>

                    {/* Categories Grid */}
                    {filteredCategories.length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-12">
                            {filteredCategories.map((category, index) => (
                                <Card key={index} className="p-4 hover:shadow-md transition-shadow cursor-pointer bg-white">
                                    <p className="text-center text-sm font-medium text-gray-800">{category}</p>
                                </Card>
                            ))}
                        </div>
                    ) : (
                        <div className="mb-12 py-16 text-center text-gray-500">
                            {t("categoriesPage.noRecordFound")}
                        </div>
                    )}

                    {/* Add Category Dialog */}
                    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                        <DialogContent>
                            <DialogHeader className="flex justify-between items-center">
                                <DialogTitle>{t("categoriesPage.dialog.title")}</DialogTitle>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 rounded-full"
                                    onClick={() => setIsDialogOpen(false)}>
                                    <X className="h-4 w-4" />
                                </Button>
                            </DialogHeader>
                            <div className="space-y-4">
                                <div>
                                    <label className="text-sm font-medium">{t("categoriesPage.dialog.categoryName")}</label>
                                    <Input
                                        value={newCategory}
                                        onChange={e => setNewCategory(e.target.value)}
                                        placeholder={t("categoriesPage.dialog.categoryNamePlaceholder")}
                                        className="mt-1"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium">{t("categoriesPage.dialog.subcategories")}</label>
                                    <Input
                                        value={newTags}
                                        onChange={e => setNewTags(e.target.value)}
                                        placeholder={t("categoriesPage.dialog.subcategoriesPlaceholder")}
                                        className="mt-1"
                                    />
                                </div>
                                <div className="flex justify-end space-x-2">
                                    <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                                        {t("categoriesPage.dialog.cancel")}
                                    </Button>
                                    <Button onClick={handleAddCategory}>{t("categoriesPage.dialog.addCategory")}</Button>
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>

                    {/* Locations Section with inline dropdown */}
                    <section>
                        <div className="flex flex-wrap items-center gap-3 mb-6">
                            <h2 className="text-xl font-semibold flex items-center gap-2">
                                <MapPin className="h-5 w-5 text-blue-600" />
                                {t("categoriesPage.locations.title")}
                            </h2>

                            {/* Country Selector */}
                            {selectedType !== "international" && (
                                <div className="w-64">
                                    <CountryDropdown
                                        selectedCountry={convertToCountryDropdownFormat(selectedCountry)}
                                        onCountrySelect={handleCountrySelect}
                                        placeholder={t("categoriesPage.locations.selectCountryPlaceholder")}
                                        className="w-full"
                                        disabled={countriesLoading}
                                        autoDetect={true}
                                        placement="top"
                                    />
                                </div>
                            )}
                        </div>

                        {selectedType === "international" ? (
                            <p className="text-gray-600">{t("categoriesPage.locations.availableWorldwide")}</p>
                        ) : (
                            <div>
                                {!selectedCountry ? (
                                    <p className="text-gray-500 text-center py-8">
                                        {t("categoriesPage.locations.selectCountryToView")}
                                    </p>
                                ) : (
                                    <div>
                                        {/* Loading states */}
                                        {(regionsLoading || citiesLoading) && (
                                            <div className="flex items-center justify-center py-8">
                                                <div className="flex items-center gap-2 text-gray-500">
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                                    {t("categoriesPage.locations.loadingRegionsAndCities")}
                                                </div>
                                            </div>
                                        )}

                                        {/* Regions and Cities Display */}
                                        {!regionsLoading && !citiesLoading && (
                                            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                                                {regions && regions.length > 0 ? (
                                                    regions
                                                        .filter((region: LocationRegion) => 
                                                            region.name.toLowerCase().includes(locationQuery.toLowerCase())
                                                        )
                                                        .map((region: LocationRegion, index: number) => {
                                                            const regionCities = region.cities || [];

                                                            return (
                                                                <Card key={region.id || index} className="p-4 bg-white">
                                                                    <h3 className="font-medium mb-3 text-gray-800">{region.name}</h3>
                                                                    <div className="space-y-2">
                                                                        {regionCities.map((city: LocationCity, cityIndex: number) => (
                                                                            <div
                                                                                key={city.id || cityIndex}
                                                                                className="text-sm text-gray-600 hover:text-blue-600 cursor-pointer transition-colors">
                                                                                {city.name}
                                                                            </div>
                                                                        ))}
                                                                        {regionCities.length === 0 && (
                                                                            <p className="text-sm text-gray-500 italic">
                                                                                {t("categoriesPage.locations.noCitiesAvailable")}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </Card>
                                                            );
                                                        })
                                                ) : (
                                                    <div className="col-span-full text-center py-8 text-gray-500">
                                                        {t("categoriesPage.locations.noRegionsAvailable", { country: selectedCountry.name })}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </section>
                </main>
                
                <SharedFooter3 />
            </div>
        </>
    );
};

export default CategoriesAndLocation;
